'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { fetchTokenBalance, fetchTransactionHistory, Transaction } from '@/services/dashboardService';
import { startTopupCheckout } from '@/lib/payments/client-checkout';

interface TokenBalanceIndicatorProps {
  className?: string;
}

// Session storage key for tracking session start time
const SESSION_START_KEY = 'session_start_timestamp';

// Get or initialize session start timestamp
const getSessionStartTime = (): string => {
  if (typeof window === 'undefined') return new Date().toISOString();
  
  let sessionStart = sessionStorage.getItem(SESSION_START_KEY);
  if (!sessionStart) {
    sessionStart = new Date().toISOString();
    sessionStorage.setItem(SESSION_START_KEY, sessionStart);
  }
  return sessionStart;
};

const TokenBalanceIndicator = ({ className = '' }: TokenBalanceIndicatorProps) => {
  const router = useRouter();
  const { user, userProfile, loading } = useAuth();
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [sessionTransactions, setSessionTransactions] = useState<Transaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState<boolean>(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [historyView, setHistoryView] = useState<'session' | 'all'>('session');
  const [displayLimit, setDisplayLimit] = useState<number>(10);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef<boolean>(false);
  const lastFetchedAtRef = useRef<number | null>(null);

  // Use persistent session start timestamp from sessionStorage
  const sessionStartRef = useRef<string>(getSessionStartTime());

  useEffect(() => {
    if (user?.id) {
      fetchBalance();
    }
  }, [user]);

  useEffect(() => {
    if (isDropdownOpen && user?.id && transactions.length === 0) {
      loadTransactions();
    }
  }, [isDropdownOpen, user]);

  useEffect(() => {
    if (user?.id && transactions.length === 0) {
      loadTransactions();
    }
  }, [user]);

  useEffect(() => {
    // Filter transactions for current session using persistent timestamp
    let sessionStart = new Date(sessionStartRef.current);
    const filtered = transactions.filter(tx => {
      const txDate = new Date(tx.created_at);
      return txDate >= sessionStart;
    });
    setSessionTransactions(filtered);
  }, [transactions]);

  useEffect(() => {
    setDisplayLimit(10);
  }, [historyView]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const fetchBalance = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const balanceData = await fetchTokenBalance(user.id);
      if (balanceData) {
        setTokenBalance(balanceData.balance);
      }
    } catch (error) {
      console.error('Error fetching token balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactions = async (force: boolean = false) => {
    if (!user?.id) return;
    if (isFetchingRef.current) return;
    if (!force && lastFetchedAtRef.current && Date.now() - lastFetchedAtRef.current < 15000) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoadingTransactions(true);
    setTransactionsError(null);
    try {
      const { data: txHistory, error } = await fetchTransactionHistory(user.id, 50);
      if (error) {
        setTransactionsError(error);
      }
      setTransactions(txHistory);
      lastFetchedAtRef.current = Date.now();
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactionsError('Error al cargar historial de tokens');
    } finally {
      setIsLoadingTransactions(false);
      isFetchingRef.current = false;
    }
  };

  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 10);
  };

  const getBalanceColor = () => {
    if (tokenBalance >= 100) return 'text-success';
    if (tokenBalance >= 50) return 'text-warning';
    return 'text-error';
  };

  const getTransactionIcon = (type: Transaction['transaction_type']) => {
    switch (type) {
      case 'purchase': case'bonus':
        return { name: 'PlusCircleIcon', color: 'text-success' };
      case 'deduction':
        return { name: 'MinusCircleIcon', color: 'text-error' };
      case 'refund':
        return { name: 'ArrowPathIcon', color: 'text-primary' };
      case 'expiry':
        return { name: 'ClockIcon', color: 'text-muted-foreground' };
      default:
        return { name: 'CurrencyDollarIcon', color: 'text-muted-foreground' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)}h`;
    
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const baseTransactions = historyView === 'session' ? sessionTransactions : transactions;
  const displayedTransactions = baseTransactions.slice(0, displayLimit);

  if (loading || !user?.id) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex cursor-pointer items-center space-x-1.5 rounded-xl border border-border bg-[rgba(255,255,255,0.72)] px-2 py-1.5 backdrop-blur-md transition-smooth hover:bg-white sm:space-x-2 sm:px-3 sm:py-2"
        aria-label="Token balance and history"
      >
        <Icon name="CurrencyDollarIcon" size={20} className={getBalanceColor()} />
        <div className="flex flex-col items-start">
          <span className="caption text-[11px] sm:text-xs text-muted-foreground">Tokens</span>
          {isLoading ? (
            <div className="h-5 w-12 bg-border animate-pulse rounded" />
          ) : (
            <span className={`font-data font-medium text-sm sm:text-base ${getBalanceColor()}`}>
              {tokenBalance}
            </span>
          )}
        </div>
        <Icon 
          name={isDropdownOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
          size={16} 
          className="text-muted-foreground ml-1" 
        />
      </button>

      {isDropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-2 flex max-h-[600px] w-[90vw] flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-elevation-lg backdrop-blur-xl sm:w-96">
            {/* Header */}
            <div className="border-b border-border bg-[rgba(255,255,255,0.7)] p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-foreground">Balance de Tokens</h3>
                <button
                  onClick={() => setIsDropdownOpen(false)}
                  className="rounded-md p-1 transition-smooth hover:bg-muted"
                  aria-label="Cerrar"
                >
                  <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CurrencyDollarIcon" size={24} className={getBalanceColor()} />
                <span className={`text-2xl font-data font-bold ${getBalanceColor()}`}>
                  {tokenBalance}
                </span>
                <span className="text-muted-foreground">tokens disponibles</span>
              </div>
            </div>

            {/* History Section */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-foreground">Historial de Tokens</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setHistoryView('session')}
                    className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                      historyView === 'session'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Sesión
                  </button>
                  <button
                    onClick={() => setHistoryView('all')}
                    className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                      historyView === 'all'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Recientes
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="caption text-muted-foreground">
                  {historyView === 'session' ? sessionTransactions.length : transactions.length} transacciones
                </span>
                <button
                  onClick={() => loadTransactions(true)}
                  className="caption text-primary hover:text-primary/80 transition-smooth"
                >
                  Actualizar
                </button>
              </div>

              {isLoadingTransactions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : transactionsError ? (
                <div className="text-center py-8">
                  <Icon name="ExclamationTriangleIcon" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">{transactionsError}</p>
                  <button
                    onClick={() => loadTransactions(true)}
                    className="mt-3 px-3 py-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
                  >
                    Reintentar
                  </button>
                </div>
              ) : baseTransactions.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="InboxIcon" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">
                    {historyView === 'session'
                      ? 'No hay transacciones en esta sesión'
                      : 'No hay transacciones recientes'}
                  </p>
                  {historyView === 'session' && transactions.length > 0 && (
                    <button
                      onClick={() => setHistoryView('all')}
                      className="mt-3 px-3 py-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
                    >
                      Ver transacciones recientes
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {displayedTransactions.map((tx) => {
                    const icon = getTransactionIcon(tx.transaction_type);
                    return (
                      <div
                        key={tx.id}
                        className="flex items-start space-x-3 p-3 bg-muted/30 rounded-md hover:bg-muted/50 transition-smooth"
                      >
                        <Icon name={icon.name as any} size={20} className={icon.color} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {tx.description}
                          </p>
                          <p className="caption text-muted-foreground">
                            {formatDate(tx.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${tx.amount > 0 ? 'text-success' : 'text-error'}`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                          </p>
                          <p className="caption text-muted-foreground">
                            Balance: {tx.balance_after}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {baseTransactions.length > displayLimit && (
                    <button
                      onClick={handleLoadMore}
                      className="w-full py-2 text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
                    >
                      Cargar más ({baseTransactions.length - displayLimit} restantes)
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-border bg-muted/50">
              <button
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
                onClick={async () => {
                  if (userProfile?.user_plan === 'emprende') {
                    try {
                      await startTopupCheckout({ productId: 'topup_80', router });
                      return;
                    } catch (error) {
                      console.error('Token indicator topup error:', error);
                    }
                  }

                  router.push('/pricing-page');
                }}
              >
                <Icon name="PlusIcon" size={20} />
                <span className="font-medium">Cargar Más Tokens</span>
              </button>
              <p className="text-center caption text-muted-foreground mt-2">
                Suscríbete o compra una recarga con Mercado Pago
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TokenBalanceIndicator;
